enum ModelType {
  Generic = "Generic",
  Specialized = "Specialized",
}

enum ModelApplication {
  AFM = "AFM",
  RMI = "RMI",
}

enum PlanningRowStatus {
  DRAFT = "draft",
  IN_APPROVAL = "inApproval",
  RELEASED = "released",
  DECOMMISSIONED = "decommissioned",
}

enum PlanningAction {
  REQUEST = "request",
  APPROVE = "approve",
  REJECT = "reject",
}

interface PlanningRow {
  rowId: number;
  ver: string;
  status: PlanningRowStatus;
  pendingStatus: string;
  protocolName: string;
  numberOfMeasurementsPerBatch: number;
  referenceMaterialId: string;
  materialNumber: string;
  globalBrand: string;
  localBrands: string[];
  drugProduct: string;
  variantName: string;
  primaryPackaging: string;
  manufacturingSite: string;
  batchNumber: string;
  modelType: ModelType;
  modelApplication: ModelApplication;
  name: string;
  libraryVariant: string;
  model: string;
}

interface PendingChanges {
  protocolName: string | null;
  numberOfMeasurementsPerBatch: number | null;
}

interface PlanningResponse {
  id: number;
  name: string;
  manufacturingSites: PlanningRow[];
  pendingChanges: PendingChanges;
}

enum ResultsRowStatus {
  NoMeasurements = "NoMeasurements",
  Inactive = "Inactive",
  Active = "Active",
}

interface ManufacturingCoverageRate {
  [materialNumber: string]: number;
}
interface CoverageRate {
  [manufacturingSite: string]: ManufacturingCoverageRate;
}

interface ResultsRow {
  firstMeasurementAt: string;
  lastMeasurementAt?: string;
  manufacturingSite: string;
  measurementCount: number;
  coverageRate: CoverageRate;
  metadataUrl: string;
  status: ResultsRowStatus;
}

interface BlendedSnapshotUpcoming {
  version: number;
  timestamp: string;
}

interface BlendedSnapshot {
  id: string;
  createdOn: string;
  modifiedOn: string;
  state: string;
  version: number;
  measurementCount: number;
}

type ProductVariantSnapshot = BlendedSnapshot;

interface ProductVariantSnapshotProps {
  version: string;
  snapshotPendingApproval: ProductVariantSnapshot;
  createdOn: string;
  productVariantId: number;
}

interface ResultsResponse {
  id: number;
  name: string;
  latestDraftSnapshot: ProductVariantSnapshot;
  latestReleasedSnapshot: ProductVariantSnapshot;
  incompleteSnapshots: ProductVariantSnapshot[];
  snapshotPendingApproval: ProductVariantSnapshot;
  rows: ResultsRow[];
  testCompletionRate: number;
  testCoverageRate: CoverageRate;
  ver: string;
}

enum ModelStatuses {
  IN_TRAINING = "IN_TRAINING",
  WAITING_APPROVAL = "WAITING_APPROVAL",
  APPROVED_PRODUCTION = "APPROVED_PRODUCTION",
  NOT_APPROVED = "NOT_APPROVED",
  APPROVED_CANDIDATE = "APPROVED_CANDIDATE",
  DECOMMISSIONED = "DECOMMISSIONED",
  REJECTED = "REJECTED",
  ERROR = "ERROR",
}

enum ModelTrainingStatus {
  TRAINING = "TRAINING",
  DONE = "DONE",
  ERROR = "ERROR",
}

interface Model {
  modelName: string;
  modelExternalName: string;
  logsAvailable: boolean;
  modelStatus: ModelStatuses;
  modelSnapshot: ProductVariantSnapshot;
  blendedSnapshot: BlendedSnapshot | null;
  runId: string;
  pendingStatus: string | null;
  modelTrainingStatus: ModelTrainingStatus;
  viewId: number;
  modelVersionId: number;
  modelMetrics: {
    [key: string]: number;
  };
  ver: string;
}

type ModelApprovalData = Pick<Model, "modelVersionId" | "modelName">;

type ModelLogsDownloadRequest = Pick<Model, "modelName" | "runId">;

interface ModelsResponse {
  id: number;
  name: string;
  rows: Model[];
  ver: string;
}

export {
  Model,
  ModelApplication,
  ModelType,
  ModelsResponse,
  ModelStatuses,
  ModelTrainingStatus,
  PlanningRow,
  PlanningResponse,
  PlanningRowStatus,
  PlanningAction,
  ResultsRow,
  ResultsResponse,
  ResultsRowStatus,
  PendingChanges,
  BlendedSnapshotUpcoming,
  BlendedSnapshot,
  ProductVariantSnapshot,
  ProductVariantSnapshotProps,
  ModelApprovalData,
  ModelLogsDownloadRequest,
};
